import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { type ISnippet } from '@principle-theorem/editor';
import {
  ISnippetFormData,
  SnippetFormComponent,
} from '@principle-theorem/ng-principle';
import {
  CurrentBrandScope,
  CurrentStafferScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  type IBreadcrumb,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { Brand, Staffer } from '@principle-theorem/principle-core';
import { type IStaffer } from '@principle-theorem/principle-core/interfaces';
import {
  addDoc,
  all$,
  type CollectionReference,
  filterUndefined,
  isPathChanged$,
  isRefChanged$,
  multiSortBy$,
  nameSorter,
  patchDoc,
  shareReplayCold,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { BehaviorSubject, combineLatest, type Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'pr-account-snippets-list',
  templateUrl: './account-snippets-list.component.html',
  styleUrls: ['./account-snippets-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountSnippetsListComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  trackBySnippet = TrackByFunctions.ref<WithRef<ISnippet>>();
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showDeleted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  snippets$: Observable<WithRef<ISnippet>[]>;
  snippetCol$: Observable<CollectionReference<ISnippet>>;
  snippetsLabel$: Observable<string>;
  breadcrumbs$: Observable<IBreadcrumb[]>;
  activeSnippet?: WithRef<ISnippet>;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService,
    private _currentBrand: CurrentBrandScope,
    private _currentStaffer: CurrentStafferScope
  ) {
    this.snippetCol$ = combineLatest([
      this._currentBrand.doc$.pipe(filterUndefined(), isRefChanged$()),
      this._currentStaffer.doc$.pipe(isPathChanged$('ref.path')),
    ]).pipe(
      map(([brand, staffer]) =>
        staffer ? Staffer.snippetCol(staffer) : Brand.snippetCol(brand)
      )
    );

    this.snippets$ = this.snippetCol$.pipe(
      switchMap((snippetCol) => all$(snippetCol)),
      multiSortBy$(nameSorter()),
      tap(() => this.loading$.next(false)),
      shareReplayCold()
    );

    this.snippetsLabel$ = this._currentStaffer.doc$.pipe(
      map((staffer) => (staffer ? 'Personal Snippets' : 'Shared Snippets'))
    );

    this.breadcrumbs$ = combineLatest([
      this._currentBrand.doc$.pipe(filterUndefined()),
      this._currentStaffer.doc$,
    ]).pipe(
      map(([brand, staffer]) => {
        if (staffer) {
          return [
            { label: 'Settings', path: '../../../../' },
            { label: brand.name },
            { label: 'My Account', path: '' },
            { label: 'Personal Snippets' },
          ];
        }
        return [
          { label: 'Settings', path: '../../../' },
          { label: brand.name },
          { label: 'Shared Snippets' },
        ];
      })
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  selectSnippet(snippet: WithRef<ISnippet>): void {
    void this._router.navigate(['./', snippet.ref.id], {
      relativeTo: this._route,
    });
  }

  async openSnippetForm(snippet?: WithRef<ISnippet>): Promise<void> {
    const staffer: WithRef<IStaffer> = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    const data: ISnippetFormData = {
      staffer,
      snippet,
    };

    const updatedSnippet = await this._dialog
      .open<SnippetFormComponent, ISnippetFormData, ISnippet>(
        SnippetFormComponent,
        DialogPresets.large({
          data,
        })
      )
      .afterClosed()
      .toPromise();

    if (!updatedSnippet) {
      return;
    }

    if (snippet) {
      await patchDoc(snippet.ref, updatedSnippet);
      this._snackBar.open('Snippet Updated');
      return;
    }

    await addDoc(await snapshot(this.snippetCol$), updatedSnippet);
    this._snackBar.open('Snippet Saved');
  }

  toggleDeleted(): void {
    const showDeleted = this.showDeleted$.value;
    if (!showDeleted) {
      this.showDeleted$.next(true);
      return;
    }
    this.showDeleted$.next(false);
  }
}
